'use client';

import { Box, Carousel, CarouselItem, Flex, freddyTheme } from '@coolblue-development/freddy';
import addItemToBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/addItemToBundleTrigger';
import removeItemFromBundleTrigger from '@coolblue-development/ga4-events/triggers/Configurator/removeItemFromBundleTrigger';
import { Plus } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import {
  SelectableBox,
  SelectableBoxGroup,
  type SelectableBoxProps,
  SquareProductImage
} from '@coolblue-development/next-patterns/client';
import isPropValid from '@emotion/is-prop-valid';
import styled from '@emotion/styled';
import { useBreakpoints } from '@hooks/useBreakpoints';

import { useConfigurator } from '../ConfiguratorContext';
import { ConfiguratorProduct } from '../types';

export default function Header() {
  const { trackEvent } = useAnalytics();
  const { mainProduct } = useConfigurator();
  const { isMobile } = useBreakpoints();
  const carouselTranslations = useFreddyTranslations('carousel');

  const {
    configurator,
    states: { selectedProductSlot, selectedUseCaseIndex, checkboxValues, configuratorValues },
    actions: { updateConfiguratorValue, setSelectedProductSlot }
  } = useConfigurator();

  const trackChanges = (index: number, isChecked: boolean) => {
    const params = {
      sub_feature: 'configurator_overlay' as const,
      index: index,
      total_indices: configurator.useCases[selectedUseCaseIndex].slots.length,
      item_id: configurator.useCases[selectedUseCaseIndex].slots[index].defaultProduct?.id || 0
    };

    if (isChecked) {
      trackEvent(removeItemFromBundleTrigger(params));
      return;
    }
    trackEvent(addItemToBundleTrigger(params));
  };

  const handleChange = (value: string) => {
    const isChecked = checkboxValues.includes(value);

    trackChanges(Number(value), isChecked);

    const updatedCheckboxValues = isChecked
      ? checkboxValues.filter(item => item !== value)
      : [...checkboxValues, value];

    const updatedConfiguratorValues = (configuratorValues.get(selectedUseCaseIndex) || []).map(
      (item, index) => ({
        ...item,
        selected: updatedCheckboxValues.includes(String(index))
      })
    );

    updateConfiguratorValue(updatedConfiguratorValues);
  };

  const Card = ({
    index,
    defaultProduct
  }: {
    index: number;
    defaultProduct: ConfiguratorProduct;
  }) => (
    <StyledSelectableBox
      onClick={event => {
        event.preventDefault();
        handleChange(String(index));
      }}
      value={String(index)}
      active={index === selectedProductSlot}
    >
      <Box
        aria-label={defaultProduct.productType?.singularName}
        component='button'
        sx={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          userSelect: 'none'
        }}
        px={0}
        onClick={event => {
          event.preventDefault();
          setSelectedProductSlot(index);
        }}
      >
        <SquareProductImage
          alt={defaultProduct.name || ''}
          imageId={defaultProduct.primaryImage?.id || ''}
          background='transparent'
        />
      </Box>
    </StyledSelectableBox>
  );

  if (isMobile) {
    return (
      <Carousel
        fullWidthUntil='md'
        translations={carouselTranslations}
        itemsInView='flexible'
        hideNavigation
        m={0}
      >
        <CarouselItem key={`mainProduct`}>
          <Flex gap={500} alignItems='center'>
            <Box p={200}>
              <SquareProductImage
                alt={mainProduct.name || ''}
                imageId={mainProduct.primaryImage?.id || ''}
                loading='lazy'
              />
            </Box>

            <Plus color={freddyTheme.color.icon.subtle} size='large' />
          </Flex>
        </CarouselItem>
        <SelectableBoxGroup
          inputPlacement='left'
          type='checkbox'
          name='configuratorOverlaySlots'
          value={checkboxValues}
        >
          {configurator.useCases[selectedUseCaseIndex].slots
            .filter(slot => slot.defaultProduct?.id)
            .map((slot, index) => {
              if (!slot.defaultProduct) {
                return null;
              }

              return (
                <CarouselItem key={`configurator-overlay-slot-${index}`}>
                  <Card index={index} defaultProduct={slot.defaultProduct} />
                </CarouselItem>
              );
            })}
        </SelectableBoxGroup>
      </Carousel>
    );
  }
  return (
    <Flex gap={500} alignItems='center' mt={400} mb={600}>
      <SquareProductImage
        alt={mainProduct.name || ''}
        imageId={mainProduct.primaryImage?.id || ''}
        loading='lazy'
      />
      <Plus color={freddyTheme.color.icon.subtle} />
      <Flex>
        <SelectableBoxGroup
          inputPlacement='left'
          type='checkbox'
          name='configuratorOverlaySlots'
          value={checkboxValues}
        >
          <Flex gap={400}>
            {configurator.useCases[selectedUseCaseIndex].slots
              .filter(slot => slot.defaultProduct?.id)
              .map((slot, index) => {
                if (!slot.defaultProduct) {
                  return null;
                }

                return (
                  <Card
                    key={`configurator-overlay-slot-${index}`}
                    index={index}
                    defaultProduct={slot.defaultProduct}
                  />
                );
              })}
          </Flex>
        </SelectableBoxGroup>
      </Flex>
    </Flex>
  );
}

interface StyledSelectableBoxProps extends SelectableBoxProps {
  active: boolean;
}

const StyledSelectableBox = styled(SelectableBox, {
  shouldForwardProp: prop => isPropValid(prop)
})<StyledSelectableBoxProps>(({ theme, active }) => ({
  label: {
    backgroundColor: active ? theme.color.background.interactiveSelected : 'unset !important',
    borderColor: active
      ? theme.color.border.interactiveSelected
      : theme.color.background.interactiveControlContrastBase,
    borderWidth: theme.borderWidth[25],
    width: '100%',
    padding: theme.spacing[200]
  },

  'input[type="checkbox"] + label': {
    padding: theme.spacing[200]
  },

  'label > div:first-of-type': {
    top: '8px',
    left: '8px'
  },

  svg: {
    position: 'absolute',
    zIndex: '2'
  }
}));
